import React, { useState } from "react";
import { loginUser, registerUser, auth } from "../config/firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import CGUScreen from "./CGUScreen";

const C = {
  bg:        "#1C3A28",
  boxBg:     "#243F2E",
  cream:     "#F8F6F1",
  mint:      "#9FE1CB",
  forest:    "#2D5A3D",
  border:    "#D3D1C7",
  text:      "#1C1C1E",
  link:      "#5DCAA5",
};

function VanSVG() {
  return (
    <svg width="140" height="80" viewBox="0 0 140 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Carrosserie */}
      <rect x="6" y="22" width="128" height="42" rx="8" fill="#2D5A3D" />
      {/* Toit arrondi */}
      <rect x="20" y="10" width="90" height="20" rx="8" fill="#3A6B4A" />
      {/* Fenêtre avant */}
      <rect x="22" y="13" width="26" height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      {/* Fenêtre milieu */}
      <rect x="54" y="13" width="26" height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      {/* Fenêtre arrière */}
      <rect x="86" y="13" width="22" height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      {/* Porte latérale */}
      <rect x="54" y="26" width="42" height="30" rx="4" fill="#346348" />
      <circle cx="94" cy="41" r="2.5" fill="#9FE1CB" />
      {/* Phare avant */}
      <rect x="8" y="34" width="10" height="7" rx="3" fill="#F8F6F1" opacity="0.9" />
      {/* Phare arrière */}
      <rect x="122" y="34" width="8" height="7" rx="3" fill="#C45A3A" opacity="0.85" />
      {/* Bas carrosserie */}
      <rect x="6" y="56" width="128" height="8" rx="0" fill="#1C3A28" />
      {/* Roue gauche */}
      <circle cx="30" cy="66" r="13" fill="#1A1A1A" />
      <circle cx="30" cy="66" r="8" fill="#2A2A2A" />
      <circle cx="30" cy="66" r="4" fill="#3D3D3D" />
      <circle cx="30" cy="66" r="1.5" fill="#9FE1CB" />
      {/* Roue droite */}
      <circle cx="108" cy="66" r="13" fill="#1A1A1A" />
      <circle cx="108" cy="66" r="8" fill="#2A2A2A" />
      <circle cx="108" cy="66" r="4" fill="#3D3D3D" />
      <circle cx="108" cy="66" r="1.5" fill="#9FE1CB" />
      {/* Calandre */}
      <rect x="8" y="44" width="10" height="3" rx="1.5" fill="#9FE1CB" opacity="0.5" />
      <rect x="8" y="49" width="10" height="2" rx="1" fill="#9FE1CB" opacity="0.35" />
    </svg>
  );
}

export default function LoginScreen({ lang = "fr", setLang }) {
  const [mode,    setMode]    = useState("login");
  const [email,   setEmail]   = useState("");
  const [pw,      setPw]      = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [btnHeld,       setBtnHeld]       = useState(false);
  const [showCGU,       setShowCGU]       = useState(false);
  const [resetMode,     setResetMode]     = useState(false);
  const [resetSent,     setResetSent]     = useState(false);
  const [resetLoading,  setResetLoading]  = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);

  const handleReset = async () => {
    if (!email) {
      setError(lang === "en" ? "Enter your email first." : "Saisissez votre email d'abord.");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError("");
    } catch (e) {
      setError(lang === "en" ? "Error sending email. Check the address." : "Erreur d'envoi. Vérifiez l'adresse.");
    }
    setResetLoading(false);
  };

  const handleLang = (l) => {
    localStorage.setItem("checkvan_lang", l);
    setLang?.(l);
  };

  const handle = async () => {
    setError(""); setLoading(true);
    try {
      if (mode === "login") await loginUser(email, pw);
      else                  await registerUser(email, pw);
    } catch (e) {
      const msgs = lang === "en" ? {
        "auth/user-not-found":       "No account with this email.",
        "auth/wrong-password":       "Incorrect password.",
        "auth/invalid-credential":   "Incorrect email or password.",
        "auth/email-already-in-use": "Email already in use.",
        "auth/weak-password":        "Password too weak (min. 6 chars).",
        "auth/invalid-email":        "Invalid email.",
      } : {
        "auth/user-not-found":       "Aucun compte avec cet email.",
        "auth/wrong-password":       "Mot de passe incorrect.",
        "auth/invalid-credential":   "Email ou mot de passe incorrect.",
        "auth/email-already-in-use": "Email déjà utilisé.",
        "auth/weak-password":        "Mot de passe trop faible (6 car. min).",
        "auth/invalid-email":        "Email invalide.",
      };
      setError(msgs[e.code] || (lang === "en" ? "Error: " : "Erreur : ") + e.message);
    }
    setLoading(false);
  };

  if (showCGU) {
    return <CGUScreen onClose={() => setShowCGU(false)} lang={lang} />;
  }

  return (
    <div style={{
      maxWidth: 430, margin: "0 auto", minHeight: "100vh",
      background: C.bg,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "0 24px",
      position: "relative",
      fontFamily: "-apple-system, 'SF Pro Display', 'Segoe UI', sans-serif",
    }}>

      {/* Sélecteur de langue */}
      <div style={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 6 }}>
        {[{ code: "fr", flag: "🇫🇷", label: "FR" }, { code: "en", flag: "🇬🇧", label: "EN" }].map(l => (
          <button
            key={l.code}
            onClick={() => handleLang(l.code)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: "none", cursor: "pointer",
              background: lang === l.code ? C.mint : "rgba(159,225,203,0.12)",
              color: lang === l.code ? C.bg : C.mint,
              fontSize: 12, fontWeight: 700, fontFamily: "inherit",
              transition: "all 0.15s",
            }}
          >
            {l.flag} {l.label}
          </button>
        ))}
      </div>

      {/* Van + Titres */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ marginBottom: 16 }}>
          <VanSVG />
        </div>
        <div style={{
          fontSize: 28, fontWeight: 900, color: C.cream,
          letterSpacing: 4, textTransform: "uppercase",
        }}>
          CheckVan
        </div>
        <div style={{
          fontSize: 14, color: C.mint, marginTop: 6,
          fontWeight: 400, letterSpacing: 0.3,
        }}>
          {lang === "en" ? "Your van's digital logbook" : "Le carnet de bord de votre van"}
        </div>
      </div>

      {/* Box centrale */}
      <div style={{
        width: "100%",
        background: C.boxBg,
        borderRadius: 14,
        padding: "24px 22px 28px",
      }}>

        {resetMode ? (
          <div>
            <div style={{ fontSize: 13, color: C.mint, marginBottom: 16, lineHeight: 1.5 }}>
              {lang === "en"
                ? "Enter your email to receive a reset link."
                : "Saisissez votre email pour recevoir un lien de réinitialisation."}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.mint, marginBottom: 6, letterSpacing: 1 }}>
              EMAIL
            </div>
            <input
              type="email"
              placeholder="vous@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleReset()}
              style={{
                width: "100%", padding: "13px 16px", marginBottom: 16,
                borderRadius: 10, border: `1.5px solid ${C.border}`,
                background: C.cream, color: C.text,
                fontSize: 15, outline: "none",
                boxSizing: "border-box", fontFamily: "inherit",
              }}
            />
            {resetSent ? (
              <div style={{
                background: "rgba(45,158,74,0.12)",
                border: "1px solid rgba(45,158,74,0.35)",
                borderRadius: 10, padding: "12px 14px", marginBottom: 16,
                fontSize: 13, color: "#2D9E4A", lineHeight: 1.5,
              }}>
                {lang === "en"
                  ? "✅ Email sent! Check your inbox (and spam)."
                  : "✅ Email envoyé ! Vérifiez votre boîte (et les spams)."}
              </div>
            ) : (
              <>
                {error && (
                  <div style={{
                    background: "rgba(196,98,45,0.12)",
                    border: "1px solid rgba(196,98,45,0.35)",
                    borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                    fontSize: 13, color: "#C4622D",
                  }}>
                    {error}
                  </div>
                )}
                <button
                  onClick={handleReset}
                  disabled={resetLoading}
                  style={{
                    width: "100%", padding: "14px 0", borderRadius: 10, border: "none",
                    background: C.cream, color: C.bg,
                    fontWeight: 700, fontSize: 15, cursor: "pointer",
                    fontFamily: "inherit", opacity: resetLoading ? 0.6 : 1,
                  }}
                >
                  {resetLoading
                    ? (lang === "en" ? "Sending..." : "Envoi...")
                    : (lang === "en" ? "Send reset link" : "Envoyer le lien")}
                </button>
              </>
            )}
            <button
              onClick={() => { setResetMode(false); setResetSent(false); setError(""); }}
              style={{
                width: "100%", marginTop: 10, padding: "11px 0",
                borderRadius: 10, border: `1px solid rgba(159,225,203,0.2)`,
                background: "transparent", color: C.mint,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {lang === "en" ? "← Back to sign in" : "← Retour à la connexion"}
            </button>
          </div>
        ) : (
          <div>
            {/* Onglets */}
            <div style={{
              display: "flex", marginBottom: 24,
              borderRadius: 10, overflow: "hidden",
              border: `1px solid rgba(159,225,203,0.2)`,
            }}>
              {[
                { id: "login",    label: lang === "en" ? "Sign in"        : "Connexion" },
                { id: "register", label: lang === "en" ? "Create account" : "Créer un compte" },
              ].map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => { setMode(m.id); setError(""); }}
                  style={{
                    flex: 1, padding: "11px 0", border: "none", cursor: "pointer",
                    fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                    background: mode === m.id ? C.cream : "transparent",
                    color: mode === m.id ? C.bg : C.mint,
                    borderRight: i === 0 ? `1px solid rgba(159,225,203,0.2)` : "none",
                    transition: "background 0.2s, color 0.2s",
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Label Email */}
            <div style={{ fontSize: 11, fontWeight: 700, color: C.mint, marginBottom: 6, letterSpacing: 1 }}>
              EMAIL
            </div>
            <input
              type="email"
              placeholder="vous@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handle()}
              style={{
                width: "100%", padding: "13px 16px", marginBottom: 16,
                borderRadius: 10, border: `1.5px solid ${C.border}`,
                background: C.cream, color: C.text,
                fontSize: 15, outline: "none",
                boxSizing: "border-box", fontFamily: "inherit",
              }}
            />

            {/* Label Mot de passe */}
            <div style={{ fontSize: 11, fontWeight: 700, color: C.mint, marginBottom: 6, letterSpacing: 1 }}>
              {lang === "en" ? "PASSWORD" : "MOT DE PASSE"}
            </div>
            <div style={{ position: "relative", marginBottom: 20 }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={pw}
                onChange={e => setPw(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handle()}
                style={{
                  width: "100%", padding: "13px 45px 13px 16px",
                  borderRadius: 10, border: `1.5px solid ${C.border}`,
                  background: C.cream, color: C.text,
                  fontSize: 15, outline: "none",
                  boxSizing: "border-box", fontFamily: "inherit",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  padding: "4px",
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.6,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "0.6"}
              >
                {showPassword ? "👁️‍🗨️" : "👁️"}
              </button>
            </div>

            {mode === "login" && (
              <div style={{ textAlign: "right", marginTop: -12, marginBottom: 16 }}>
                <button
                  onClick={() => { setResetMode(true); setError(""); setResetSent(false); }}
                  style={{
                    background: "none", border: "none", padding: 0,
                    fontSize: 12, color: C.link, cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(93,202,165,0.4)",
                    fontFamily: "inherit",
                  }}
                >
                  {lang === "en" ? "Forgot password?" : "Mot de passe oublié ?"}
                </button>
              </div>
            )}

            {error && (
              <div style={{
                background: "rgba(196,98,45,0.12)",
                border: "1px solid rgba(196,98,45,0.35)",
                borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                fontSize: 13, color: "#C4622D",
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handle}
              disabled={loading}
              onMouseDown={() => setBtnHeld(true)}
              onMouseUp={() => setBtnHeld(false)}
              onMouseLeave={() => setBtnHeld(false)}
              style={{
                width: "100%", padding: "14px 0",
                borderRadius: 10, border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 700, fontSize: 15, fontFamily: "inherit",
                background: btnHeld ? C.forest : C.cream,
                color: btnHeld ? C.cream : C.bg,
                opacity: loading ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {loading
                ? (lang === "en" ? "Loading..." : "Chargement...")
                : mode === "login"
                  ? (lang === "en" ? "Sign in"        : "Se connecter")
                  : (lang === "en" ? "Create account" : "Créer le compte")}
            </button>
          </div>
        )}
      </div>

      {/* Lien Conditions */}
      <div style={{ marginTop: 24, textAlign: "center" }}>
        <button
          onClick={() => setShowCGU(true)}
          style={{
            background: "none", border: "none", padding: "8px 12px",
            fontSize: 12, color: C.link,
            cursor: "pointer", textDecoration: "underline",
            textDecorationColor: "rgba(93,202,165,0.4)",
            fontFamily: "inherit",
          }}
        >
          {lang === "en" ? "Terms of use" : "Conditions d'utilisation"}
        </button>
      </div>

    </div>
  );
}
